import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import ClassIcon from "@mui/icons-material/Class";
import { useAdmin } from "../../context/AdminContext";
import DbService from "../../services/DbService";
import { useHandleError } from "../../hooks/useHandleError";

interface StatCardProps {
  title: string;
  count: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, count, icon, color }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="textSecondary">
            {title}
          </Typography>
          <Typography variant="h4">{count}</Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: "50%",
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const handleError = useHandleError();
  const {
    schools,
    principals,
    teachers,
    setSchoolsToAdminProvider,
    setPrincipalsToAdminProvider,
    setTeachersToAdminProvider,
  } = useAdmin();
  const [studentCount, setStudentCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    const fetchData = async () => {
      try {
        const [schoolRes, principalRes, teacherRes, studentRes] =
          await Promise.all([
            DbService.getSchools(),
            DbService.getPrincipals(),
            DbService.getTeachers(),
            DbService.getStudents(),
          ]);
        setSchoolsToAdminProvider(schoolRes.data);
        setPrincipalsToAdminProvider(principalRes.data);
        setTeachersToAdminProvider(teacherRes.data);
        setStudentCount(studentRes.data?.length || 0);
      } catch (err) {
        handleError(err);
      }
    };
    fetchData();
    setLoaded(true);
  }, [loaded]);

  return (
    <Grid container spacing={3} style={{ marginBottom: "2rem" }}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Admin Dashboard
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Schools"
          count={schools.length}
          icon={<SchoolIcon sx={{ color: "white" }} />}
          color="#5c6bc0"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Teachers"
          count={teachers.length}
          icon={<PersonIcon sx={{ color: "white" }} />}
          color="#26a69a"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Students"
          count={studentCount}
          icon={<GroupIcon sx={{ color: "white" }} />}
          color="#ef5350"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Principals"
          count={principals.length}
          icon={<ClassIcon sx={{ color: "white" }} />}
          color="#ffa726"
        />
      </Grid>
    </Grid>
  );
};

export default AdminDashboard;
